import { useEffect, useState } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import { Briefcase, Users, Building2, CheckCircle, Clock, AlertCircle } from 'lucide-react';

const PRIORITY_COLOR = {
  urgent: 'bg-red-100 text-red-700',
  high:   'bg-orange-100 text-orange-700',
  normal: 'bg-blue-100 text-blue-700',
  low:    'bg-gray-100 text-gray-600',
};

const STATUS_COLOR = {
  open:      'bg-yellow-100 text-yellow-700',
  assigned:  'bg-blue-100 text-blue-700',
  en_route:  'bg-purple-100 text-purple-700',
  on_site:   'bg-indigo-100 text-indigo-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const STATUS_NEXT = {
  open:     ['assigned'],
  assigned: ['en_route', 'cancelled'],
  en_route: ['on_site'],
  on_site:  ['completed'],
};

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>{icon}</div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats]   = useState(null);
  const [jobs, setJobs]     = useState([]);
  const [workers, setWorkers] = useState([]);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    api.get('/opsive/stats').then(setStats).catch(e => toast.error(e.message));
    api.get('/opsive/workers').then(setWorkers).catch(() => {});
    loadJobs();
  }, []); // eslint-disable-line

  const loadJobs = () => {
    api.get('/opsive/jobs').then(setJobs).catch(e => toast.error(e.message));
  };

  const assign = async (job_id, worker_id, worker_name) => {
    try {
      await api.patch(`/opsive/jobs/${job_id}`, { assigned_worker_id: worker_id, assigned_worker_name: worker_name });
      loadJobs();
      toast.success('Worker assigned');
    } catch (e) { toast.error(e.message); }
  };

  const moveStatus = async (job_id, status) => {
    try {
      await api.patch(`/opsive/jobs/${job_id}`, { status });
      loadJobs();
      toast.success(`Job moved to ${status.replace('_', ' ')}`);
    } catch (e) { toast.error(e.message); }
  };

  const filtered = filter === 'active'
    ? jobs.filter(j => ['open','assigned','en_route','on_site'].includes(j.status))
    : filter === 'completed'
    ? jobs.filter(j => j.status === 'completed')
    : jobs;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard icon={<Briefcase className="w-5 h-5 text-blue-600" />} label="Total Jobs" value={stats?.total_jobs} color="bg-blue-50" />
        <StatCard icon={<AlertCircle className="w-5 h-5 text-yellow-600" />} label="Open" value={stats?.open} color="bg-yellow-50" />
        <StatCard icon={<Clock className="w-5 h-5 text-purple-600" />} label="Active" value={stats?.active} color="bg-purple-50" />
        <StatCard icon={<CheckCircle className="w-5 h-5 text-green-600" />} label="Done Today" value={stats?.completed_today} color="bg-green-50" />
        <StatCard icon={<Building2 className="w-5 h-5 text-indigo-600" />} label="Clients" value={stats?.clients} color="bg-indigo-50" />
        <StatCard icon={<Users className="w-5 h-5 text-gray-600" />} label="Workers" value={stats?.workers} color="bg-gray-100" />
      </div>

      {/* Dispatch Board */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Dispatch Board</h2>
          <div className="flex gap-1">
            {['active','completed','all'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                className={`text-xs px-3 py-1 rounded-full font-medium capitalize transition-colors ${
                  filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >{f}</button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No jobs in this view. Create one in the Jobs tab.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map(job => (
              <div key={job.job_id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{job.title}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[job.status]}`}>
                      {job.status.replace('_', ' ')}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_COLOR[job.priority]}`}>
                      {job.priority}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {job.client_name || 'No client'} {job.address ? `· ${job.address}` : ''}
                    {job.assigned_worker_name ? ` · ${job.assigned_worker_name}` : ' · Unassigned'}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  {/* Assign dropdown (only for open/unassigned) */}
                  {job.status === 'open' && workers.length > 0 && (
                    <select
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
                      defaultValue=""
                      onChange={e => {
                        const w = workers.find(x => x.user_id === e.target.value);
                        if (w) assign(job.job_id, w.user_id, w.name);
                      }}
                    >
                      <option value="" disabled>Assign worker…</option>
                      {workers.map(w => (
                        <option key={w.user_id} value={w.user_id}>{w.name}</option>
                      ))}
                    </select>
                  )}

                  {/* Status advance buttons */}
                  {(STATUS_NEXT[job.status] || []).map(next => (
                    <button
                      key={next}
                      onClick={() => moveStatus(job.job_id, next)}
                      className="text-xs px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-700 transition-colors capitalize"
                    >
                      → {next.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
